import { createApp, defineAsyncComponent } from 'vue';
import { createBootstrap } from 'bootstrap-vue-next';
import { languages } from './locales/index';
import { createI18n } from 'vue-i18n';
import App from './App.vue';
import { VueSignaturePad } from 'vue-signature-pad';
import * as Sentry from '@sentry/vue';
import BootstrapMixin from './bootstrap/mixin';
import loading from '@/directives/loading';
import router from './router';
import store from './store';
import { applyCustomCss } from '@/services/customCss';

import VueSidebarMenu from 'vue-sidebar-menu';

// Tiny
import tinymce from 'tinymce/tinymce';

// Toast
import Vue3Toastify, { toast } from 'vue3-toastify';
import 'vue3-toastify/dist/index.css';

// Dayjs
import dayjs from 'dayjs';
import 'dayjs/locale/nl';
import 'dayjs/locale/de';
import 'dayjs/locale/en';

// Dayjs plugins
import relativeTime from 'dayjs/plugin/relativeTime';

// Import model to inject filters into Model.js
import Model from './models/Model.js';

// Set tiny baseURL
tinymce.baseURL = '/tinymce-assets';

dayjs.extend(relativeTime);

const i18n = createI18n({
    locale: 'nl',
    fallbackLng: 'en',
    messages: Object.assign(languages)
});

const app = createApp(App)
    .use(createBootstrap())
    .use(BootstrapMixin)
    .use(router)
    .use(store)
    .use(i18n)
    .use(VueSidebarMenu)
    .use(Vue3Toastify, {
        autoClose: 3000,
        position: toast.POSITION.TOP_RIGHT // Additional options here
    })
    .use({
        // Fix for i18n methods not properly binding when unmounted quickly on navigation
        // https://github.com/intlify/vue-i18n/issues/990#issuecomment-1123565067
        install (appInner) {
            appInner.config.globalProperties.$t = i18n.global.t;
            appInner.config.globalProperties.$tc = i18n.global.tc;
        }
    })
    .component('VueSignaturePad', VueSignaturePad)
    .directive('loading', loading)
    .provide('dayjs', dayjs);

const sentryDsn = import.meta.env.VITE_SENTRY_DSN || '';
const sentryEnv = import.meta.env.VITE_SENTRY_ENV || '';

Sentry.init({
    app,
    dsn: sentryDsn,
    environment: sentryEnv,
    integrations: [
        Sentry.browserTracingIntegration({ router }),
        Sentry.replayIntegration({
            maskAllText: false,
            blockAllMedia: false,
            maskAllInputs: false
        })
    ],
    tracesSampleRate: 0,
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0 // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});

// Pas CSS toe zodra de styling-config binnenkomt (ook als dit later gebeurt)
store.subscribe((mutation, state) => {
    if (mutation.type === 'SET_STYLING_CONFIG') {
        applyCustomCss(state.stylingConfig?.custom_css);
    }
});

// Als stylingConfig al ergens eerder gezet is (bijv. vanuit cache), ook meteen toepassen
applyCustomCss(store.state.stylingConfig?.custom_css);

// Automatically register components from the `shared` directory
const components = import.meta.glob('./shared/**/*.vue'); // Adjust path as needed

Object.entries(components).forEach(([path, resolver]) => {
    const componentName = path
        .split('/')
        .pop()
        .replace(/\.\w+$/, '') // Remove the file extension
        .replace(/-(\w)/g, (_, c) => (c ? c.toUpperCase() : '')) // Convert kebab-case to PascalCase
        .replace(/^\w/, (c) => c.toUpperCase()); // Capitalize the first letter

    app.component(componentName, defineAsyncComponent(resolver));
});

app.mount('#app');

app.config.globalProperties.$dayjs = dayjs;
app.config.globalProperties.$filters = {
    toCurrency (value) {
        if (isNaN(value) || value === null) {
            return '';
        }

        const formatter = new Intl.NumberFormat('nl-NL', {
            style: 'currency',
            currency: 'EUR',
            minimumFractionDigits: 2
        });

        return formatter.format(value);
    },

    ellipsis (value, length) {
        if (!value) {
            return '';
        }

        const dots = value.length > length ? '...' : '';
        return value.substring(0, length) + dots;
    },

    minutesToTime (value) {
        value = isNaN(value) ? 0 : value;
        return (value < 0 ? '-' : '') + String(Math.floor(Math.abs(value / 60))).padStart(2, '0') + ':' + String(Math.round(Math.abs(value % 60))).padStart(2, '0');
    },

    toCamelCase (value) {
        return value.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
    },

    toSnakeCase (value) {
        if (!value) {
            return '';
        }

        return String(value)
            .replace(/^[^A-Za-z0-9]*|[^A-Za-z0-9]*$/g, '')
            .replace(/([a-z])([A-Z])/g, (m, a, b) => a + '_' + b.toLowerCase())
            .replace(/[^A-Za-z0-9]+|_+/g, '_')
            .toLowerCase();
    },

    toQuantity (value) {
        value = isNaN(value) ? 0 : Number(value);

        if (value % 1 !== 0) {
            value = value.toFixed(2);
        }

        return String(value);
    }
};

// Inject filters into Model.js
const filters = app.config.globalProperties.$filters;
Model.setFilters(filters);
